import {useCallback, useContext, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import cx from 'classnames';
import {pluralize} from '~/common/utils';
import {SignUpSheetContext} from '~/SignUpSheets/context/SignUpSheetContext';
import {SET_SELECTED_SIGNUPS, SET_SIGNUPS} from '~/SignUpSheets/reducers/sheet/constants';
import {
  selectExpired,
  selectIsOrganizer,
  selectPrivacyOptions,
  selectSheetStatus,
  selectSignupQuestions,
  selectSignups,
} from '~/SignUpSheets/reducers/sheet/selectors';
import {useRemoveSignup} from '~/SignUpSheets/hooks/useRemoveSignup';
import {Typography} from '~library/atoms/Typography';
import {Button} from '~library/atoms/Button';
import {TrashIcon} from '~library/svg/TrashIcon';
import {SmsLineIcon} from '~library/svg/SmsLineIcon';
import {InboxIcon} from '~library/svg/InboxIcon';
import {ResponsiveModal} from '~library/organisms/ResponsiveModal';
import {AccountLineIcon} from '~library/svg/AccountLineIcon';
import {MealOptionsSmIcon} from '~library/svg/MealOptionsSmIcon';
import {MobileDeviceIcon} from '~library/svg/MobileDeviceIcon';
import {AnswersSection} from './AnswersSection/AnswersSection';

import {pebble} from '~libSass/base/_exports.sass';
import './FilledSlot.sass';

export const FilledSlot = ({
  userId,
  name,
  comment,
  email,
  phone,
  signupsCount,
  matchingUser,
  slotId,
  signUpOptionId,
  answers,
}) => {
  const [showConfirmDeleteVolunteer, setShowConfirmDeleteVolunteer] = useState(false);
  const {flow} = useContext(SignUpSheetContext);
  const isOrganizer = useSelector(selectIsOrganizer);
  const signups = useSelector(selectSignups);
  const questions = useSelector(selectSignupQuestions);
  const dispatch = useDispatch();
  const [handleRemoveSignup] = useRemoveSignup();
  const past = useSelector((s) => selectExpired(s, {uuid: slotId || signUpOptionId}));
  const status = useSelector(selectSheetStatus);
  const {hide_names: privateNames, hide_comments: privateComments} =
    useSelector(selectPrivacyOptions);

  const isOrganizerOrMatchingUser = isOrganizer || matchingUser;

  const canceled = status === 'canceled';
  const expired = useMemo(() => past || canceled, [past, canceled]);

  const filledSlotAnswers = useMemo(
    () =>
      answers.map(({answer, question_id: questionId}) => {
        let formattedAnswer = '';
        let icon = null;

        const {label, ...question} = questions.find((q) => q.uuid === questionId);

        if (label === 'children_names') {
          formattedAnswer = `Children: ${answer}`;
          icon = <AccountLineIcon color={pebble} ratio={0.5} />;
        } else if (label === 'allergies') {
          formattedAnswer = `Allergies: ${answer}`;
          icon = <MealOptionsSmIcon color={pebble} ratio={0.5} />;
        } else if (label === 'potluck_item') {
          formattedAnswer = `Potluck: ${answer}`;
          icon = <MealOptionsSmIcon color={pebble} ratio={0.5} />;
        }
        return {...question, answer: formattedAnswer, icon};
      }),
    [answers, questions]
  );

  const handleTrashCanClick = useCallback(() => {
    const updatedSignups = signups.map((su) => {
      let isDeleted = false;
      let slotToDelete = null;
      const matchingUserId = su.user_id === userId;

      if (su.slot_id) {
        slotToDelete = su.slot_id === slotId && matchingUserId ? su : null;
      } else if (su.signup_option) {
        slotToDelete = su.signup_option === signUpOptionId && matchingUserId ? su : null;
      }

      if (slotToDelete) {
        if (slotToDelete.id) {
          // If it contains an ID, it's a saved signup, so remove it from the backend
          handleRemoveSignup(slotToDelete);
        }
        isDeleted = true;
      }

      return {...su, deleted: isDeleted};
    });

    const signupsToKeep = updatedSignups.filter((su) => !su.deleted);

    dispatch({type: SET_SIGNUPS, payload: signupsToKeep});
    dispatch({type: SET_SELECTED_SIGNUPS, payload: signupsToKeep});
  }, [handleRemoveSignup, dispatch, signups, userId, slotId, signUpOptionId]);

  const allAnswers = useMemo(
    () => [
      {
        label: 'comment',
        answer: comment,
        icon: <SmsLineIcon color={pebble} ratio={0.5} />,
        visible_to_all: !privateComments,
      },
      {
        label: 'email',
        answer: email,
        icon: <InboxIcon color={pebble} ratio={0.5} />,
        visible_to_all: false,
      },
      {
        label: 'phone',
        answer: phone,
        icon: <MobileDeviceIcon color={pebble} ratio={0.5} />,
        visible_to_all: false,
      },
      ...filledSlotAnswers,
    ],
    [comment, privateComments, email, phone, filledSlotAnswers]
  );

  const publicAnswers = useMemo(
    () => allAnswers.filter((a) => a.answer && a.visible_to_all),
    [allAnswers]
  );

  const privateAnswers = useMemo(
    () => allAnswers.filter((a) => a.answer && !a.visible_to_all),
    [allAnswers]
  );

  const displayName = useMemo(() => {
    if ((privateNames && !isOrganizerOrMatchingUser) || !name) {
      return 'Volunteer';
    }

    return name;
  }, [privateNames, isOrganizerOrMatchingUser, name]);

  return (
    <div className="filled-slot__container">
      <div className={cx('filled-slot__item', {'filled-slot__item--own': matchingUser})}>
        <div
          className="filled-slot__name-comment-wrapper"
          data-qa-id="signup-row-user"
          data-qa-value={name}
        >
          <div className="filled-slot__name-row">
            <div>
              <Typography variant="list2" size="large" as="span" data-qa-id="signup-row-name">
                {displayName}
              </Typography>
              {matchingUser && (
                <Typography variant="paragraph2" as="span">
                  {' (you)'}
                </Typography>
              )}
            </div>

            <div className="filled-slot__count-and-delete">
              <Typography color="charcoal" variant="list1" size="small">
                {pluralize('slot', signupsCount)}
              </Typography>
              {flow === 'edit' && isOrganizer && !expired ? (
                <Button
                  variant="transparent"
                  data-qa-id="filled-slot__delete-btn"
                  onClick={setShowConfirmDeleteVolunteer}
                  aria-label={`Delete ${pluralize('signup', signupsCount)} for ${name}`}
                  className="filled-slot__delete-btn"
                >
                  <TrashIcon color={pebble} ratio={0.5} />
                </Button>
              ) : null}
            </div>
          </div>

          <AnswersSection visibility="public" answers={publicAnswers} matchingUser={matchingUser} />

          <AnswersSection
            visibility="private"
            answers={privateAnswers}
            matchingUser={matchingUser}
          />
        </div>
      </div>
      {showConfirmDeleteVolunteer && (
        <ResponsiveModal
          isOpen
          title={`Are you sure you want to remove ${name} from this slot?`}
          primaryText="Yes, remove them"
          primaryAction={handleTrashCanClick}
          secondaryText="No, don't remove them"
          secondaryAction={() => {
            setShowConfirmDeleteVolunteer(false);
          }}
          onClose={() => setShowConfirmDeleteVolunteer(false)}
          body={`Once you remove ${name}, we’ll notify them via email.`}
          equalButtons
        />
      )}
    </div>
  );
};
